/* eslint-disable no-mixed-operators */
import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import ReactLoading from 'react-loading';
import { ToastContainer } from 'react-toastify';
import DashboardController from '../../../../controller/admin/dashboard';
import LaporanKepesantrenanController, {
  useLaporanKepesantrenan,
} from '../../../../controller/admin/laporan/data_kepesantrenan';
import SidebarWithHeader from '../../../../components/Admin/SidebarWithHeader';
import {
  Box,
  Button,
  Flex,
  Input,
  Select,
} from '@chakra-ui/react';

const customStyles = {
  headCells: {
    style: {
      backgroundColor: '#F2FFFB',
      fontWeight: '600',
      fontSize: '13px',
    },
  },
};

const CustomLoader = () => (
  <div style={{ padding: '24px', textAlign: 'center' }}>
    <ReactLoading type={'bars'} color={'green'} height={80} width={80} />
    <div>Loading Data...</div>
  </div>
);

const PERAN_OPTIONS = [
  { value: 'santri', label: 'Santri' },
  { value: 'pengajar', label: 'Pengajar' },
  { value: 'pengasuh', label: 'Pengasuh' },
  { value: 'tenaga_kependidikan', label: 'Tenaga Kependidikan' },
  { value: 'lainnya', label: 'Lainnya' },
];

const WrapperMain: React.FC = () => {
  const {
    fetchData,
    exportData,
    setPage,
    setPageRow,
    setNamaPesantren,
    setPeran,
  } = useLaporanKepesantrenan();
  const { listData, totalData, isLoading, isExporting, page, rows, namaPesantren, peran } =
    useLaporanKepesantrenan().state;

  const [searchInput, setSearchInput] = useState('');
  const [peranInput, setPeranInput] = useState('');

  useEffect(() => {
    fetchData(page, rows, namaPesantren, peran);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rows]);

  const handleSearch = () => {
    setNamaPesantren(searchInput);
    setPeran(peranInput);
    setPage(1);
    fetchData(1, rows, searchInput, peranInput);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerRowsChange = async (newPerPage: number) => {
    setPageRow(newPerPage);
  };

  const columns = [
    {
      name: 'No.',
      cell: (_row: any, index: number) => (page - 1) * rows + (index + 1),
      grow: 0,
      width: '60px',
    },
    {
      name: 'Nama Pelatihan',
      selector: (row: any) => row.judulPelatihan,
      wrap: true,
      minWidth: '180px',
    },
    {
      name: 'Nama Peserta',
      selector: (row: any) => row.namaLengkap,
      wrap: true,
      minWidth: '150px',
    },
    {
      name: 'Dari Pesantren',
      cell: (row: any) => (
        <span
          style={{
            color: row.isPesantren ? '#019166' : '#F86E70',
            fontWeight: 600,
          }}
        >
          {row.isPesantren ? 'Ya' : 'Tidak'}
        </span>
      ),
      wrap: true,
      width: '110px',
    },
    {
      name: 'Nama Pesantren',
      selector: (row: any) => row.namaPesantren ?? '-',
      wrap: true,
      minWidth: '160px',
    },
    {
      name: 'NSP',
      selector: (row: any) => row.nsp ?? '-',
      wrap: true,
    },
    {
      name: 'Provinsi',
      selector: (row: any) => row.namaProvinsi ?? '-',
      wrap: true,
    },
    {
      name: 'Kabupaten/Kota',
      selector: (row: any) => row.namaKabupaten ?? '-',
      wrap: true,
    },
    {
      name: 'Kecamatan',
      selector: (row: any) => row.namaKecamatan ?? '-',
      wrap: true,
    },
    {
      name: 'Peran',
      selector: (row: any) =>
        row.peran ? row.peran.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '-',
      wrap: true,
    },
    {
      name: 'Tahun Bergabung',
      selector: (row: any) => row.tahunBergabung ?? '-',
      wrap: true,
      width: '130px',
    },
    {
      name: 'Status Mukim',
      cell: (row: any) => {
        if (row.statusMukim === null) return '-';
        return row.statusMukim === 1 ? 'Mukim' : 'Tidak Mukim';
      },
      wrap: true,
      width: '110px',
    },
  ];

  return (
    <SidebarWithHeader title="Laporan Data Kepesantrenan">
      <Box bg="white" p={4} borderRadius="lg" mb={4} shadow="sm">
        <Flex gap={2} wrap="wrap" alignItems="center">
          <Input
            placeholder="Cari nama pesantren..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            maxW="300px"
          />
          <Select
            placeholder="Semua Peran"
            value={peranInput}
            onChange={(e) => setPeranInput(e.target.value)}
            maxW="200px"
          >
            {PERAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button colorScheme="green" onClick={handleSearch} isLoading={isLoading}>
            Cari
          </Button>
          <Button
            colorScheme="teal"
            variant="outline"
            onClick={() => exportData(namaPesantren, peran)}
            isLoading={isExporting}
          >
            Export Excel
          </Button>
        </Flex>
      </Box>

      <Box bg="white" p={4} borderRadius="lg" shadow="sm" overflowX="auto">
        <DataTable
          columns={columns}
          data={listData}
          progressPending={isLoading}
          progressComponent={<CustomLoader />}
          pagination
          paginationServer
          paginationTotalRows={totalData}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          customStyles={customStyles}
          noDataComponent={
            <Box py={8} color="gray.500">
              Tidak ada data ditemukan
            </Box>
          }
        />
      </Box>
    </SidebarWithHeader>
  );
};

const LaporanDataKepesantrenan: React.FC = () => {
  return (
    <DashboardController.Provider>
      <LaporanKepesantrenanController.Provider>
        <ToastContainer position="top-center" style={{ width: '60%', marginTop: '5%' }} />
        <WrapperMain />
      </LaporanKepesantrenanController.Provider>
    </DashboardController.Provider>
  );
};

export default LaporanDataKepesantrenan;
